import assert from 'assert';
import type { VideoJsPlayer } from 'video.js';
import videojs from 'video.js';
import type { VttTile, VttType } from './utils';
import { fetchVttJSON } from './utils';
import './style.scss';

interface VttThumbnailsOptions {
  src: string;
  width?: number;
  height?: number;
}

export class VttThumbnails extends videojs.getPlugin('plugin') {
  private options: VttThumbnailsOptions = { src: '' };
  private vttConfig?: VttType;
  private thumbnailContainerUI?: HTMLDivElement;
  private thumbnailUI?: HTMLDivElement;

  public constructor(player: VideoJsPlayer, options?: VttThumbnailsOptions) {
    super(player, options);
  }

  private get progressBar(): HTMLDivElement {
    return <HTMLDivElement>this.player.$('.vjs-progress-control');
  }

  private get progressHolder(): HTMLDivElement {
    return <HTMLDivElement>this.player.$('.vjs-progress-holder');
  }

  private get width(): number {
    return this.options.width ?? this.vttConfig?.tile_width ?? 0;
  }

  public update(options: VttThumbnailsOptions): void {
    this.options = options;
    this.init();
  }

  /**
   * Setup thumnail element to the progress bar.
   */
  private init(): void {
    this.cleanup();

    void fetchVttJSON(this.options.src)
      .then((data) => {
        this.vttConfig = data;
        this.setup();
      })
      .catch(() => {
        // Ignore.
      });
  }

  private cleanup(): void {
    this.thumbnailUI?.remove();
    this.thumbnailContainerUI?.remove();
  }

  private setup(): void {
    assert(this.vttConfig);

    const { tile_width, tile_height } = this.vttConfig;

    // Hide mouse display.
    try {
      this.player.$('.vjs-mouse-display').classList.add('vjs-hidden');
    } catch {
      // Ignore.
    }

    this.thumbnailContainerUI = document.createElement('div');
    this.thumbnailContainerUI.classList.add('vjs-vtt-thumbnail-container');

    this.thumbnailUI = document.createElement('div');
    this.thumbnailUI.classList.add('vjs-thumbnail');
    this.thumbnailContainerUI.append(this.thumbnailUI);

    this.thumbnailContainerUI.style.width = `${tile_width}px`;
    this.thumbnailContainerUI.style.height = `${tile_height}px`;

    // Scale down to the size required.

    this.progressHolder.append(this.thumbnailContainerUI);

    this.progressBar.addEventListener('mouseenter', this.onMouseEnter);
    this.progressBar.addEventListener('mouseleave', this.onMouseLeave);
  }

  private readonly onMouseEnter = (): void => {
    this.progressBar.addEventListener('mousemove', this.onMouseMove);
  };

  private readonly onMouseLeave = (): void => {
    this.progressBar.removeEventListener('mousemove', this.onMouseMove);
  };

  private readonly onMouseMove = (event: Event): void => {
    const { x: xFraction } = videojs.dom.getPointerPosition(this.progressHolder, event);

    const vttTile = this.getVttTile(xFraction);

    if (!vttTile) {
      return;
    }

    assert(this.vttConfig);

    const { width: progressHolderWidth } = this.progressHolder.getBoundingClientRect();
    const xPos = xFraction * progressHolderWidth - this.width / 2;

    this.updateThumnail(Math.min(Math.max(xPos, 0), progressHolderWidth - this.width), vttTile);
  };

  private updateThumnail(xPos: number, tile: VttTile): void {
    assert(this.thumbnailContainerUI && this.thumbnailUI);
    assert(this.vttConfig);

    const scale = this.options.width
      ? this.options.width / this.vttConfig.tile_width
      : this.options.height
      ? this.options.height / this.vttConfig.tile_height
      : undefined;

    if (scale) {
      this.thumbnailUI.style.transform = `scale(${scale})`;
    }

    this.thumbnailContainerUI.style.transform = `translateX(${xPos}px)`;
    this.thumbnailUI.style.background = `url('${this.vttConfig.url}') no-repeat -${tile.x}px -${tile.y}px`;
  }

  private getVttTile(fraction: number): VttTile | undefined {
    assert(this.vttConfig);

    const time = this.vttConfig.duration * fraction;

    let [prevTile] = this.vttConfig.tiles;

    const {
      tiles: [, ...tiles]
    } = this.vttConfig;

    for (const tile of tiles) {
      if (time >= prevTile.start && time < tile.start) {
        return tile;
      }

      prevTile = tile;
    }

    return undefined;
  }
}
